import React from 'react';
import useDrawingContext from '@/components/common/Forms/Questions/Drawing/store/useDrawingContext';
import { scaleValue } from '@/components/common/Forms/Questions/Drawing/Toolbar/styles';
import { containerStyle } from '@/components/common/Forms/Questions/Drawing/Toolbar/ShapesButton/Shapes';
import cn from '@/utils/cn';
import AddQuestionImage from '@/components/admin/QuestionImage/Add';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import Spinner from 'components/common/Spinner';
import useFlags from '@/hooks/useFlags';

interface ImagesProps {
  category: string;
  images: any;
  isLoadingImages: boolean;
  questionId: number;
  setCategory: (category: string) => void;
  setImages: (images: any) => void;
  setPopoverIsOpen: (isOpen: boolean) => void;
  userType: UserType;
}

const imgStyle = (toolbarEl: React.MutableRefObject<HTMLDivElement>) => ({
  maxHeight: `${scaleValue(toolbarEl, 80, 150)}px`,
  maxWidth: `${scaleValue(toolbarEl, 80, 150)}px`,
});

export const imageWithModelId = (imageModel: { id: number; image: Record<string, any> }) => (
  { ...imageModel.image, imageModelId: imageModel.id }
);

const Images = ({
  category, images, isLoadingImages, questionId, setCategory, setImages, setPopoverIsOpen, userType,
}: ImagesProps) => {
  const setMode = useDrawingContext(state => state.setMode);
  const setShapeUrl = useDrawingContext(state => state.setShapeUrl);
  const toolbarEl = useDrawingContext(state => state.toolbarEl);
  const builder = useDrawingContext(state => state.builder);
  const locale = useDrawingContext(state => state.locale);
  const { spanishDrawingQuestionTextImages } = useFlags();
  const buttonScale = `${scaleValue(toolbarEl, 90, 175)}px`;

  const displayUrl = image => (
    spanishDrawingQuestionTextImages && locale === 'es' && image.image_es_file_name ? image.es_url : image.url
  );

  const handleClick = (image) => {
    setMode('shape');
    setShapeUrl(displayUrl(image), image.imageModelId);
    setPopoverIsOpen(false);
    setCategory('');
  };

  const afterSubmit = () => {
    Axios.get(Routes.plato_api_image_models_path(
      { cliparts: true, for_model: { model_id: questionId, model_type: 'Question' } },
    )).then((data) => {
      setImages(data.data.data.map(imageWithModelId));
      window.dispatchEvent(new CustomEvent('imageAdded'));
    });
  };

  const renderAddImageButton = () => {
    if (userType === 'Student' || !builder) return <></>;

    return (
      <AddQuestionImage
        afterSubmit={afterSubmit}
        buttonText="Add Image"
        excludeImageFileTypes={['image/gif']}
        forAnswerKey={false}
        forQuestionBuilder={builder}
        mediaType="clipart"
        modalOverlayClassName="tw-z-9999"
        modelId={questionId}
        modelType="Question"
        userType={userType}
      />
    );
  };

  return (
    <div
      className={cn(
        'tw-absolute tw-top-0 tw-bg-white tw-rounded tw-border tw-shadow-md tw-outline tw-outline-1',
        'tw-outline-gray-300 tw-p-2 tw-overflow-auto',
      )}
      role="menu"
      style={containerStyle(toolbarEl)}
    >
      <h2
        className="tw-text-center tw-pb-2"
        style={{ fontSize: `${scaleValue(toolbarEl, 120, 140)}%` }}
      >
        {category}
      </h2>
      <div className="tw-absolute tw-top-2 tw-right-2">
        {images?.length > 0 && renderAddImageButton()}
      </div>
      {isLoadingImages && <Spinner />}
      {!isLoadingImages && (
        images?.length > 0 ? (
          <div className="tw-flex tw-flex-wrap">
            {images.map(image => (
              <button
                key={image.id}
                className={cn(
                  'tw-bg-transparent tw-border-0 hover:tw-cursor-pointer hover:tw-bg-light-grey hover:tw-outline',
                  'hover:tw-outline-1 tw-inline-flex tw-items-center tw-justify-center',
                )}
                role="menuitem"
                style={{ height: buttonScale, width: buttonScale }}
                type="button"
                onClick={() => handleClick(image)}
              >
                <img
                  alt={image.category}
                  className="tw-p-1"
                  src={displayUrl(image)}
                  style={imgStyle(toolbarEl)}
                />
              </button>
            ))}
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
            <span className="tw-my-8">
              No question images were found.
            </span>
            {renderAddImageButton()}
          </div>
        )
      )}
    </div>
  );
};

export default Images;
